import { Finding, TelemetryPoint } from '../types';
import {
  E3_HARDWARE_H_FOV,
  E3_HARDWARE_V_FOV,
  E3_EFFECTIVE_FOV_MULTIPLIER,
  E3_CAMERA_WALL_OFFSET,
  calculateSurfaceFootprint,
} from './telemetryService';

// ─── QUATERNION APPLICATION (ported from TrajectoryMapT) ─────────────────────
// Rotates a vector by a quaternion. Same formula as in the trajectory viewer,
// extracted here as a plain helper so the dedup service doesn't depend on React.
const applyQuaternion = (p: { x: number; y: number; z: number }, q: { x: number; y: number; z: number; w: number }) => {
  const { x, y, z } = p;
  const { x: qx, y: qy, z: qz, w: qw } = q;
  const tx = 2 * (qy * z - qz * y);
  const ty = 2 * (qz * x - qx * z);
  const tz = 2 * (qx * y - qy * x);
  return {
    x: x + qw * tx + (qy * tz - qz * ty),
    y: y + qw * ty + (qz * tx - qx * tz),
    z: z + qw * tz + (qx * ty - qy * tx),
  };
};

// ─── STANDOFF (ported from TrajectoryMapT.getOpticalDistanceToCloud) ─────────
// Given the drone pose + point cloud, returns the perpendicular camera-to-wall
// distance in meters. Identical algorithm to the one used by the viewer — walk
// the cloud, find the closest point in the camera's look direction (dot > 0.95).
// Falls back to cylindrical default if no cloud is provided.
export const getStandoffAtPose = (
  pos: { x: number; y: number; z: number },
  rot: { x: number; y: number; z: number; w: number },
  modelPoints: TelemetryPoint[] | undefined,
  assetCenter: { x: number; y: number } | undefined,
  defaultRadius: number
): number => {
  if (!modelPoints || modelPoints.length === 0) {
    if (!assetCenter) return 1.5;
    const dx = pos.x - assetCenter.x;
    const dy = pos.y - assetCenter.y;
    return Math.abs(defaultRadius - Math.sqrt(dx * dx + dy * dy));
  }

  const vLook = applyQuaternion({ x: 1, y: 0, z: 0 }, rot);
  let minDist = 5.0;
  let found = false;

  const step = Math.max(1, Math.ceil(modelPoints.length / 1000));
  for (let i = 0; i < modelPoints.length; i += step) {
    const p = modelPoints[i];
    const dx = p.x - pos.x;
    const dy = p.y - pos.y;
    const dz = p.z - pos.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d > 5.0 || d < 0.1) continue;

    const dot = (dx * vLook.x + dy * vLook.y + dz * vLook.z) / d;
    if (dot > 0.95) {
      if (!found || d < minDist) {
        minDist = d;
        found = true;
      }
    }
  }

  return found ? minDist : 1.5;
};

// ─── WALL ANCHOR PROJECTION ──────────────────────────────────────────────────
// Projects a finding's bounding-box center to a world point on the vessel wall.
//
// Assumes perpendicular flight (drone camera axis ≈ normal to wall). That makes
// the math straightforward: the drone's forward ray hits the wall at `standoff`,
// and the bbox offset from image center corresponds directly to lateral/vertical
// offset on the wall, scaled by the footprint width/height at that standoff.
//
// Returns { x, y, z } in the same world frame as the trajectory and LAS cloud,
// or null if the required pose data isn't available.
export interface WallAnchor { x: number; y: number; z: number; standoff: number }

export const computeWallAnchor = (
  finding: Finding,
  pose: { x: number; y: number; z: number; qx: number; qy: number; qz: number; qw: number } | null | undefined,
  modelPoints: TelemetryPoint[] | undefined,
  assetCenter: { x: number; y: number } | undefined,
  assetRadius: number
): WallAnchor | null => {
  if (!pose || !finding.box || finding.box.length < 4) return null;

  const pos = { x: pose.x, y: pose.y, z: pose.z };
  const rot = { x: pose.qx, y: pose.qy, z: pose.qz, w: pose.qw };

  // Perpendicular distance from camera to wall (real measurement from cloud)
  const realStandoff = getStandoffAtPose(pos, rot, modelPoints, assetCenter, assetRadius);
  const opticalStandoff = Math.max(0.01, realStandoff - E3_CAMERA_WALL_OFFSET);

  // Physical wall footprint at this standoff (cm → m)
  const footprint = calculateSurfaceFootprint(opticalStandoff, assetRadius);
  const footprintW = footprint.widthCm / 100;   // meters, horizontal
  const footprintH = footprint.heightCm / 100;  // meters, vertical

  // Bounding-box center normalized to -0.5..+0.5 (image center at origin)
  const [ymin, xmin, ymax, xmax] = finding.box;
  const cx_norm = (xmin + xmax) / 2 / 1000 - 0.5;   // left = negative, right = positive
  const cy_norm = (ymin + ymax) / 2 / 1000 - 0.5;   // top = negative, bottom = positive

  // Camera forward in world frame — the perpendicular-to-wall direction
  const forward = applyQuaternion({ x: 1, y: 0, z: 0 }, rot);
  // Camera right in world frame — used for lateral (horizontal) offset on wall
  const right = applyQuaternion({ x: 0, y: 1, z: 0 }, rot);
  // Camera up — but we assume wall-perpendicular flight so vertical maps to -Z
  // in world space (image-down = more negative Z).

  // Actual camera origin (drone body is 25cm behind the camera)
  const camOffset = applyQuaternion({ x: E3_CAMERA_WALL_OFFSET, y: 0, z: 0 }, rot);
  const camX = pos.x + camOffset.x;
  const camY = pos.y + camOffset.y;
  const camZ = pos.z + camOffset.z;

  // Wall-center intersection (where camera's forward ray meets the wall)
  const wallCenterX = camX + forward.x * opticalStandoff;
  const wallCenterY = camY + forward.y * opticalStandoff;
  const wallCenterZ = camZ + forward.z * opticalStandoff;

  // Lateral offset on wall (along camera-right)
  const lateral = cx_norm * footprintW;
  // Vertical offset on wall — assume image-down = world-down (Z axis) since
  // perpendicular flight keeps the camera roughly upright.
  const vertical = cy_norm * footprintH;

  return {
    x: wallCenterX + right.x * lateral,
    y: wallCenterY + right.y * lateral,
    z: wallCenterZ - vertical,
    standoff: opticalStandoff,
  };
};

// ─── DEDUP ───────────────────────────────────────────────────────────────────
// Clusters findings by type + wall-anchor proximity. Within each cluster, keeps
// the single "best" finding (highest confidence, falling back to first seen).
// Returns both the deduplicated list and the merge groups for UI display.
//
// thresholdCm: two findings of the same type whose wallAnchor points are within
// this distance on the wall are considered the same physical defect.

export interface MergeGroup {
  canonical: FindingWithAnchor;
  duplicates: FindingWithAnchor[];
}
export interface DedupResult {
  deduplicated: FindingWithAnchor[];
  mergeGroups: MergeGroup[];   // only clusters with >1 member
  unmatched: FindingWithAnchor[]; // findings with no wallAnchor — passed through untouched
  stats: {
    total: number;
    unique: number;
    duplicatesRemoved: number;
  };
}

export interface FindingWithAnchor extends Finding {
  wallAnchor?: WallAnchor;
}

// Case-insensitive type comparison, strips whitespace
const normalizeType = (t: string | undefined) => (t || '').toLowerCase().replace(/\s+/g, ' ').trim();

const dist3d = (a: WallAnchor, b: WallAnchor) => {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

export const deduplicateFindings = (
  findings: FindingWithAnchor[],
  thresholdCm: number = 10
): DedupResult => {
  const thresholdM = thresholdCm / 100;

  const unmatched: FindingWithAnchor[] = [];
  const anchored: FindingWithAnchor[] = [];

  for (const f of findings) {
    if (f.wallAnchor) anchored.push(f);
    else unmatched.push(f);
  }

  // Greedy clustering: walk findings in order; each finding either joins an
  // existing cluster (same type, within threshold of cluster's canonical) or
  // starts a new one. O(N*K) with N findings and K clusters — fine for
  // thousands of findings.
  const clusters: FindingWithAnchor[][] = [];

  for (const f of anchored) {
    const fType = normalizeType(f.type);
    let placed = false;
    for (const cluster of clusters) {
      const head = cluster[0];
      if (normalizeType(head.type) !== fType) continue;
      if (dist3d(f.wallAnchor!, head.wallAnchor!) <= thresholdM) {
        cluster.push(f);
        placed = true;
        break;
      }
    }
    if (!placed) clusters.push([f]);
  }

  // Pick a canonical per cluster: highest confidence, else first.
  const getConfidence = (f: FindingWithAnchor): number => {
    const v = (f as any).confidence ?? (f as any).confidenceScore ?? 0;
    return typeof v === 'number' ? v : 0;
  };

  const deduplicated: FindingWithAnchor[] = [];
  const mergeGroups: MergeGroup[] = [];

  for (const cluster of clusters) {
    if (cluster.length === 1) {
      deduplicated.push(cluster[0]);
      continue;
    }
    const sorted = [...cluster].sort((a, b) => getConfidence(b) - getConfidence(a));
    const canonical = sorted[0];
    const duplicates = sorted.slice(1);
    deduplicated.push(canonical);
    mergeGroups.push({ canonical, duplicates });
  }

  // Unmatched findings (no wallAnchor) pass through untouched
  deduplicated.push(...unmatched);

  return {
    deduplicated,
    mergeGroups,
    unmatched,
    stats: {
      total: findings.length,
      unique: deduplicated.length,
      duplicatesRemoved: findings.length - deduplicated.length,
    },
  };
};
